import React, { useState, useMemo, useEffect } from "react";

import { ChevronUp, ChevronDown } from "assets/icons";
import { IExpandableFormGroup, ICheckboxField } from "types";

interface useExpandableOptionsProps {
  value?: string[];
  options: ICheckboxField[];
  expandableOptions: IExpandableFormGroup;
}

export const useExpandableOptions = ({
  value,
  options,
  expandableOptions,
}: useExpandableOptionsProps) => {
  const {
    expandable,
    expandableLimit = options.length,
    expandableMoreText,
    expandableLessText,
  } = expandableOptions;

  // undefined = not applicable because there are less options than the limit
  const [isExpanded, setExpanded] = useState<boolean | undefined>(
    expandable && expandableLimit < options.length ? false : undefined
  );

  const visibleOptions = useMemo(() => {
    if (!expandable) return options;

    // undefined = not applicable so return all options
    if (isExpanded === undefined) return options;

    if (isExpanded) return options;

    return options.slice(0, expandableLimit);
  }, [options, expandable, expandableLimit, isExpanded]);

  // we don't want this value to update once rendered
  const hiddenOptionIsActive = useMemo(
    () =>
      !!options
        .slice(visibleOptions.length, options.length) // hidden options
        .filter((option) => value?.includes(option.value)).length, // all hidden active options
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // open options if a hidden option is active
  useEffect(() => {
    if (hiddenOptionIsActive) {
      setExpanded(true);
    }
  }, [hiddenOptionIsActive]);

  const nbHiddenOptions: number = useMemo(
    () => options.length - expandableLimit,
    [expandableLimit, options.length]
  );

  const renderExpandableButton = () => (
    <div>
      {expandableMoreText && isExpanded === false && (
        <button
          className="link is-small"
          type="button"
          onClick={() => setExpanded(true)}
        >
          <span className="link__inner">
            <span className="link__text">
              {expandableMoreText?.replace("@number", String(nbHiddenOptions))}
            </span>
            <ChevronDown />
          </span>
        </button>
      )}

      {expandableLessText && isExpanded === true && (
        <button
          className="link is-small"
          type="button"
          onClick={() => setExpanded(false)}
        >
          <span className="link__inner">
            <span className="link__text">
              {expandableLessText?.replace("@number", String(nbHiddenOptions))}
            </span>
            <ChevronUp />
          </span>
        </button>
      )}
    </div>
  );

  return {
    isExpanded,
    setExpanded,
    visibleOptions,
    nbHiddenOptions,
    hiddenOptionIsActive,
    renderExpandableButton,
  };
};
